import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, catchError, tap, throwError } from 'rxjs';
import { NodeApiCallService } from './node-api-call.service';
import { CacheService } from './cache.service';
import { endPoints } from '../api-end-points';
import { StorageKey, StorageType } from '../storage-key';
import { StorageService } from './storage.service';
import { FirebaseAuthService } from './firebase-auth.service';

export interface AuthUser {
  id?: number | string | null;
  name?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  [key: string]: any;
}

export interface AuthResponse {
  success?: boolean;
  isSuccess?: boolean;
  token?: string | null;
  accessToken?: string | null;
  tokenType?: string | null;
  expiresIn?: number | string | null;
  user?: AuthUser | null;
  legacyUser?: AuthUser | null;
  userId?: number | string | null;
  provider?: string | null;
  firebaseUid?: string | null;
  profileRequired?: boolean | null;
  isNewUser?: boolean | null;
  expiration?: string | Date | null;
  expiresAt?: string | Date | null;
  expiry?: string | Date | null;
  tokenExpiration?: string | Date | null;
  message?: string | null;
  redirectTo?: string | null;
  nextRoute?: string | null;
  verificationId?: number | string | null;
  id?: number | string | null;
  [key: string]: any;
}

export interface FirebaseLoginResponse extends AuthResponse {
  success?: boolean;
  token?: string | null;
  accessToken?: string | null;
  tokenType?: string | null;
  expiresIn?: number | string | null;
  provider?: 'phone' | 'email' | string | null;
  firebaseUid?: string | null;
  profileRequired?: boolean | null;
  isNewUser?: boolean | null;
  user?: AuthUser | null;
}

export interface FirebaseLoginRequest {
  firebaseIdToken: string;
  email?: string;
  name?: string;
  mobile?: string;
  sourceNameId?: number;
  registrationUrl?: string;
  ipAddress?: string;
  ipCountry?: string;
  city?: string;
}

export interface OtpRequest {
  phoneNumber: string;
  verificationId?: number | string;
  name?: string;

  ipAddress?: string;
  ipCountry?: string;
  sourceNameId?: number;
  registrationUrl?: string;
}

export interface VerifyOtpRequest extends OtpRequest {
  otp: string;
}

export interface EmailOtpRequest {
  email: string;
  name?: string;
  mobile?: string;
  userTypeId?: number;
  sourceNameId?: number;
  registrationUrl?: string;
  ipCountry?: string;
  ipAddress?: string;
  city?: string;
}

export interface VerifyEmailOtpRequest extends EmailOtpRequest {
  otp: string;
}

export interface SaveLeadRequest {
  UserId:Number;
  Name: string;
  Email: string;
  country_dial_code: string;
  Mobile: string;
  Source: string;
  Medium: string;
  Campaign: string;
  RegistrationUrl: string;
  UserQuery: string;
}

export interface AuthState {
  token: string | null;
  expiration: string | null;
  user: AuthUser | null;
  userId: number | null;
  provider?: string | null;
  firebaseUid?: string | null;
  profileRequired?: boolean;
  isNewUser?: boolean;
  tokenType?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authStateSubject = new BehaviorSubject<AuthState>(this.readStoredState());
  readonly authState$ = this.authStateSubject.asObservable();

  constructor(
    private nodeApiService: NodeApiCallService,
    private firebaseAuthService: FirebaseAuthService,
    private storage: StorageService,
    private cacheService: CacheService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    const currentState = this.authStateSubject.value;
    if (this.hasExpiredState(currentState)) {
      this.clearAuthState(true);
      return;
    }

    this.cacheService.onUserDetailsChange(currentState.user);
    this.cacheService.setIsUserLoggedIn(Boolean(currentState.token));
  }

  sendOtp(
    mobileNumber: string,
    verificationId?: number | string,
    ipAddress?: string,
    ipCountry?: string,
    sourceNameId?: number,
    registrationUrl?: string
  ): Observable<AuthResponse> {
    return this.nodeApiService.Post(
      endPoints.AUTH_SEND_OTP,
      this.buildOtpPayload(
        mobileNumber,
        verificationId,
        ipAddress,
        ipCountry,
        sourceNameId,
        registrationUrl
      )
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  verifyOtp(
    mobileNumber: string,
    otp: string,
    verificationId?: number | string,
    ipAddress?: string,
    ipCountry?: string,
    sourceNameId?: number,
    registrationUrl?: string
  ): Observable<AuthResponse> {
    const payload: VerifyOtpRequest = {
      ...this.buildOtpPayload(
        mobileNumber,
        verificationId,
        ipAddress,
        ipCountry,
        sourceNameId,
        registrationUrl
      ),
      name: this.buildTemporaryName(mobileNumber),
      otp: otp.trim(),
    };

    return this.nodeApiService.Post(endPoints.AUTH_VERIFY_OTP, payload).pipe(
      tap((response: AuthResponse) => {
        if (this.isSuccessfulAuthResponse(response)) {
          this.handleAuthResponse(response);
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  resendOtp(
    mobileNumber: string,
    verificationId?: number | string,
    ipAddress?: string,
    ipCountry?: string,
    sourceNameId?: number,
    registrationUrl?: string
  ): Observable<AuthResponse> {
    const payload = this.buildOtpPayload(
      mobileNumber,
      verificationId,
      ipAddress,
      ipCountry,
      sourceNameId,
      registrationUrl
    );

    return this.nodeApiService.Post(endPoints.AUTH_RESEND_OTP, payload).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          // Some backend builds expose only /auth/send-otp, so reuse it as a resend fallback.
          return this.nodeApiService.Post(endPoints.AUTH_SEND_OTP, payload);
        }

        return throwError(() => error);
      })
    );
  }

  sendEmailOtp(request: EmailOtpRequest): Observable<AuthResponse> {
    const payload = this.buildEmailOtpPayload(request);

    return this.nodeApiService.Post(endPoints.AUTH_SEND_EMAIL_OTP, payload).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  verifyEmailOtp(request: VerifyEmailOtpRequest): Observable<AuthResponse> {
    const payload = {
      ...this.buildEmailOtpPayload(request),
      otp: String(request.otp ?? '').trim(),
    };

    return this.nodeApiService.Post(endPoints.AUTH_VERIFY_EMAIL_OTP, payload).pipe(
      tap((response: AuthResponse) => {
        if (this.isSuccessfulAuthResponse(response)) {
          this.handleAuthResponse(response);
        }
      }),
      catchError((error) => throwError(() => error))
    );
  }

  resendEmailOtp(request: EmailOtpRequest): Observable<AuthResponse> {
    const payload = this.buildEmailOtpPayload(request);

    return this.nodeApiService.Post(endPoints.AUTH_RESEND_EMAIL_OTP, payload).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          return this.nodeApiService.Post(endPoints.AUTH_SEND_EMAIL_OTP, payload);
        }

        return throwError(() => error);
      })
    );
  }

  saveLead(request: SaveLeadRequest): Observable<AuthResponse> {
    return this.nodeApiService.Post(endPoints.AUTH_SAVE_LEAD, request);
  }

  async exchangeFirebaseLogin(
    request: FirebaseLoginRequest
  ): Promise<FirebaseLoginResponse> {
    const payload = this.buildFirebaseLoginPayload(request);
    const headers = {
      'Content-Type': 'application/json',
      'X-Portal': 'frontend',
    };

    return this.exchangeFirebaseLoginWithRetry(payload, headers);
  }

  exchangeFirebaseIdToken(firebaseIdToken: string): Observable<FirebaseLoginResponse> {
    const payload = {
      firebaseIdToken: firebaseIdToken.trim(),
    };

    return this.nodeApiService.Post(endPoints.AUTH_FIREBASE_LOGIN, payload, {
      'Content-Type': 'application/json',
      'X-Portal': 'frontend',
    }).pipe(
      tap((response: FirebaseLoginResponse) => {
        if (this.isSuccessfulAuthResponse(response)) {
          this.handleAuthResponse(response);
        }
      }),
      catchError((error) => {
        if (error?.status === 404) {
          return this.nodeApiService.Post(endPoints.AUTH_FIREBASE_LOGIN_LEGACY, payload, {
            'Content-Type': 'application/json',
            'X-Portal': 'frontend',
          }).pipe(
            tap((response: FirebaseLoginResponse) => {
              if (this.isSuccessfulAuthResponse(response)) {
                this.handleAuthResponse(response);
              }
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private async exchangeFirebaseLoginWithRetry(
    payload: FirebaseLoginRequest,
    headers: Record<string, string>
  ): Promise<FirebaseLoginResponse> {
    try {
      const response = await firstValueFrom(
        this.nodeApiService.Post(endPoints.AUTH_FIREBASE_LOGIN, payload, headers)
      );
      const normalizedResponse = this.normalizeFirebaseLoginResponse(response);

      if (this.isSuccessfulAuthResponse(normalizedResponse)) {
        this.handleAuthResponse(normalizedResponse);
      }

      return normalizedResponse;
    } catch (error: any) {
      if (error?.status === 404) {
        const legacyResponse = await firstValueFrom(
          this.nodeApiService.Post(endPoints.AUTH_FIREBASE_LOGIN_LEGACY, payload, headers)
        );
        const normalizedLegacyResponse = this.normalizeFirebaseLoginResponse(legacyResponse);

        if (this.isSuccessfulAuthResponse(normalizedLegacyResponse)) {
          this.handleAuthResponse(normalizedLegacyResponse);
        }

        return normalizedLegacyResponse;
      }

      if (error?.status === 401) {
        const refreshedToken = await this.firebaseAuthService.getFreshIdToken();
        const retryPayload = {
          ...payload,
          firebaseIdToken: refreshedToken.trim(),
        };

        const retryResponse = await firstValueFrom(
          this.nodeApiService.Post(endPoints.AUTH_FIREBASE_LOGIN, retryPayload, headers)
        );
        const normalizedRetryResponse = this.normalizeFirebaseLoginResponse(retryResponse);

        if (this.isSuccessfulAuthResponse(normalizedRetryResponse)) {
          this.handleAuthResponse(normalizedRetryResponse);
        }

        return normalizedRetryResponse;
      }

      throw error;
    }
  }

  private buildFirebaseLoginPayload(request: FirebaseLoginRequest): FirebaseLoginRequest {
    return {
      firebaseIdToken: String(request.firebaseIdToken ?? '').trim(),
      ...(request.email ? { email: String(request.email).trim().toLowerCase() } : {}),
      ...(request.name ? { name: String(request.name).trim() } : {}),
      ...(request.mobile ? { mobile: this.normalizeMobileNumber(String(request.mobile).trim()) } : {}),
      ...(typeof request.sourceNameId === 'number' ? { sourceNameId: request.sourceNameId } : {}),
      ...(request.registrationUrl ? { registrationUrl: String(request.registrationUrl).trim() } : {}),
      ...(String(request.ipAddress ?? '').trim() ? { ipAddress: String(request.ipAddress).trim() } : {}),
      ...(String(request.ipCountry ?? '').trim() ? { ipCountry: String(request.ipCountry).trim() } : {}),
      ...(String(request.city ?? '').trim() ? { city: String(request.city).trim() } : {}),
    };
  }

  handleAuthResponse(response: AuthResponse): number | null {
    const token = this.extractAuthToken(response);
    if (!token) {
      return null;
    }

    const user = response?.user ?? response?.legacyUser ?? this.authStateSubject.value.user ?? null;
    const userId = this.extractUserId(response?.userId ?? user);
    const expiration = this.normalizeExpiration(
      response?.expiration ??
      response?.expiresAt ??
      response?.expiry ??
      response?.tokenExpiration ??
      response?.expiresIn
    );
    const currentState = this.authStateSubject.value;

    this.persistState({
      token,
      expiration,
      user,
      userId,
      provider: response?.provider ?? currentState.provider ?? null,
      firebaseUid: response?.firebaseUid ?? currentState.firebaseUid ?? null,
      profileRequired: Boolean(response?.profileRequired) || this.requiresProfileSetup(response, user),
      isNewUser: Boolean(response?.isNewUser),
      tokenType: response?.tokenType ?? currentState.tokenType ?? 'Bearer',
    });

    return userId;
  }

  setUser(user: AuthUser | null): void {
    const currentState = this.authStateSubject.value;
    this.persistState({
      ...currentState,
      user,
      userId: this.extractUserId(user) ?? currentState.userId,
    });
  }

  getAccessToken(): string | null {
    return this.getToken();
  }

  getToken(): string | null {
    const currentState = this.authStateSubject.value;
    if (!currentState.token) {
      return null;
    }

    if (this.hasTokenExpired(currentState.token, currentState.expiration)) {
      this.clearAuthState(true);
      return null;
    }

    return currentState.token;
  }

  getExpiration(): string | null {
    return this.authStateSubject.value.expiration;
  }

  getUser(): AuthUser | null {
    return this.authStateSubject.value.user;
  }

  getUserId(): number | null {
    return this.authStateSubject.value.userId;
  }

  getProvider(): string | null {
    return this.authStateSubject.value.provider ?? null;
  }

  getFirebaseUid(): string | null {
    return this.authStateSubject.value.firebaseUid ?? null;
  }

  getTokenType(): string {
    return this.authStateSubject.value.tokenType ?? 'Bearer';
  }

  requiresProfileCompletion(): boolean {
    const currentState = this.authStateSubject.value;
    return Boolean(currentState.profileRequired || currentState.isNewUser || this.hasMissingGrade(currentState.user));
  }

  getIsNewUser(): boolean {
    return Boolean(this.authStateSubject.value.isNewUser);
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  isAuthenticated(): boolean {
    return Boolean(this.getToken());
  }

  logout(): void {
    this.clearAuthState(true);
  }

  private buildOtpPayload(
    mobileNumber: string,
    verificationId?: number | string,
    ipAddress?: string,
    ipCountry?: string,
    sourceNameId?: number,
    registrationUrl?: string
  ): OtpRequest {
    const payload: OtpRequest = {
      phoneNumber: this.normalizeMobileNumber(mobileNumber),
    };

    const normalizedVerificationId = String(verificationId ?? '').trim();

    if (normalizedVerificationId) {
      payload.verificationId = Number.isNaN(Number(normalizedVerificationId))
        ? normalizedVerificationId
        : Number(normalizedVerificationId);
    }

    if (String(ipAddress ?? '').trim()) {
      payload.ipAddress = String(ipAddress).trim();
    }

    if (String(ipCountry ?? '').trim()) {
      payload.ipCountry = String(ipCountry).trim();
    }

    if (typeof sourceNameId === 'number') {
      payload.sourceNameId = sourceNameId;
    }

    if (String(registrationUrl ?? '').trim()) {
      payload.registrationUrl = String(registrationUrl).trim();
    }

    return payload;
  }

  private buildEmailOtpPayload(request: EmailOtpRequest): EmailOtpRequest {
    const payload: EmailOtpRequest = {
      email: String(request.email ?? '').trim().toLowerCase(),
    };

    if (String(request.name ?? '').trim()) {
      payload.name = String(request.name).trim();
    }

    if (String(request.mobile ?? '').trim()) {
      payload.mobile = this.normalizeMobileNumber(String(request.mobile).trim());
    }

    if (typeof request.userTypeId === 'number') {
      payload.userTypeId = request.userTypeId;
    }

    if (typeof request.sourceNameId === 'number') {
      payload.sourceNameId = request.sourceNameId;
    }

    if (String(request.registrationUrl ?? '').trim()) {
      payload.registrationUrl = String(request.registrationUrl).trim();
    }

    if (String(request.ipCountry ?? '').trim()) {
      payload.ipCountry = String(request.ipCountry).trim();
    }

    if (String(request.ipAddress ?? '').trim()) {
      payload.ipAddress = String(request.ipAddress).trim();
    }

    if (String(request.city ?? '').trim()) {
      payload.city = String(request.city).trim();
    }

    return payload;
  }

  private buildTemporaryName(mobileNumber: string): string {
    const digits = String(mobileNumber ?? '').replace(/\D/g, '');
    const suffix = digits.slice(-4) || 'User';
    return `User ${suffix}`;
  }

  private normalizeMobileNumber(mobileNumber: string): string {
    const rawValue = String(mobileNumber ?? '').trim();
    if (!rawValue) {
      return '';
    }

    if (rawValue.includes('-')) {
      const [dialCode, ...localParts] = rawValue.split('-');
      const localNumber = localParts.join('-').replace(/\D/g, '');
      const normalizedDialCode = dialCode.startsWith('+') ? dialCode : `+${dialCode.replace(/\D/g, '')}`;
      return `${normalizedDialCode}-${localNumber}`;
    }

    if (rawValue.startsWith('+')) {
      const digits = rawValue.replace(/\D/g, '');
      if (digits.length > 10) {
        const countryCode = digits.slice(0, digits.length - 10);
        const localNumber = digits.slice(-10);
        return `+${countryCode}-${localNumber}`;
      }
    }

    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      return rawValue;
    }

    const localNumber = digits.length > 10 ? digits.slice(-10) : digits;
    return `+91-${localNumber}`;
  }

  private readStoredState(): AuthState {
    const token = this.storage.getToken() ?? this.storage.getItem(StorageKey.TOKEN, StorageType.LOCAL);
    const expirationDate = this.storage.getTokenExpiry();
    const user = this.readStoredUser();

    return {
      token,
      expiration: expirationDate ? expirationDate.toISOString() : null,
      user,
      userId: this.extractUserId(user),
      provider: null,
      firebaseUid: null,
      profileRequired: false,
      isNewUser: false,
      tokenType: 'Bearer',
    };
  }

  private readStoredUser(): AuthUser | null {
    const storedUser = this.storage.getItem(StorageKey.USER, StorageType.LOCAL);
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }

  private persistState(state: AuthState): void {
    if (isPlatformBrowser(this.platformId)) {
      if (state.token) {
        this.storage.setItem(StorageKey.TOKEN, state.token, StorageType.LOCAL);
      } else {
        this.storage.clearToken();
      }

      if (state.token && state.expiration) {
        this.storage.setTokenWithExpiry(state.token, state.expiration);
      } else {
        this.storage.remove(StorageKey.TOKEN_EXPIRATION, StorageType.LOCAL);
      }

      if (state.user) {
        this.storage.setItem(StorageKey.USER, JSON.stringify(state.user), StorageType.LOCAL);
      } else {
        this.storage.remove(StorageKey.USER, StorageType.LOCAL);
      }

      this.storage.setItem(StorageKey.IS_LOGGED_IN, state.token ? 'true' : 'false', StorageType.LOCAL);
    }

    this.authStateSubject.next(state);
    this.cacheService.onUserDetailsChange(state.user);
    this.cacheService.setIsUserLoggedIn(Boolean(state.token));
  }

  private clearAuthState(emitState: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      this.storage.clearToken();
      this.storage.remove(StorageKey.USER, StorageType.LOCAL);
      this.storage.remove(StorageKey.MOBILE_NO, StorageType.LOCAL);
      this.storage.setItem(StorageKey.IS_LOGGED_IN, 'false', StorageType.LOCAL);
    }

    const emptyState: AuthState = {
      token: null,
      expiration: null,
      user: null,
      userId: null,
      provider: null,
      firebaseUid: null,
      profileRequired: false,
      isNewUser: false,
      tokenType: 'Bearer',
    };

    if (emitState) {
      this.authStateSubject.next(emptyState);
      this.cacheService.onUserDetailsChange(null);
      this.cacheService.setIsUserLoggedIn(false);
    }
  }

  private hasExpiredState(state: AuthState): boolean {
    return Boolean(state.token && this.hasTokenExpired(state.token, state.expiration));
  }

  private hasTokenExpired(token: string, expiration: string | null): boolean {
    if (expiration) {
      const expirationTime = new Date(expiration).getTime();
      if (!Number.isNaN(expirationTime)) {
        return Date.now() >= expirationTime;
      }
    }

    const jwtExpiry = this.extractJwtExpiry(token);
    if (!jwtExpiry) {
      return false;
    }

    return Date.now() >= jwtExpiry;
  }

  private extractJwtExpiry(token: string): number | null {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    try {
      const decoded = this.decodeBase64Url(payload);
      if (!decoded) {
        return null;
      }

      const parsed = JSON.parse(decoded);
      const exp = Number(parsed?.exp);
      if (!Number.isFinite(exp)) {
        return null;
      }

      return exp * 1000;
    } catch {
      return null;
    }
  }

  private decodeBase64Url(value: string): string | null {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));

    if (typeof globalThis.atob !== 'function') {
      return null;
    }

    try {
      return globalThis.atob(`${base64}${padding}`);
    } catch {
      return null;
    }
  }

  private extractUserId(source: any): number | null {
    const rawUserId =
      typeof source === 'number' || typeof source === 'string'
        ? source
        : source?.userId ?? source?.id ?? source?.Id ?? null;

    if (rawUserId === null || rawUserId === undefined || rawUserId === '') {
      return null;
    }

    const parsedUserId = Number(rawUserId);
    return Number.isNaN(parsedUserId) ? null : parsedUserId;
  }

  private normalizeExpiration(expiration: any): string | null {
    if (!expiration) {
      return null;
    }

    if (typeof expiration === 'number' && Number.isFinite(expiration)) {
      return new Date(Date.now() + expiration * 1000).toISOString();
    }

    if (typeof expiration === 'string' && /^\d+$/.test(expiration)) {
      return new Date(Date.now() + Number(expiration) * 1000).toISOString();
    }

    return expiration instanceof Date ? expiration.toISOString() : String(expiration);
  }

  private isSuccessfulAuthResponse(response: AuthResponse | null | undefined): boolean {
    if (!response) {
      return false;
    }

    if (response.success === false) {
      return false;
    }

    return Boolean(this.extractAuthToken(response));
  }

  private extractAuthToken(response: AuthResponse | null | undefined): string | null {
    if (!response) {
      return null;
    }

    const responseAny = response as any;
    const tokenCandidates = [
      response.token,
      response.accessToken,
      responseAny['Token'],
      responseAny['AccessToken'],
      responseAny['accessToken'],
      responseAny['access_token'],
      responseAny['jwt'],
      responseAny['jwtToken'],
      responseAny['authToken'],
      responseAny['data']?.['token'],
      responseAny['data']?.['accessToken'],
      responseAny['data']?.['Token'],
      responseAny['data']?.['AccessToken'],
      responseAny['data']?.['access_token'],
      responseAny['data']?.['jwt'],
      responseAny['data']?.['jwtToken'],
      responseAny['data']?.['authToken'],
    ];

    const token = tokenCandidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return token ? token.trim() : null;
  }

  private normalizeFirebaseLoginResponse(response: any): FirebaseLoginResponse {
    const responseAny = response ?? {};
    const legacyUser = this.normalizeAuthUser(responseAny.legacyUser ?? null);
    const user = this.normalizeAuthUser(
      responseAny.user ??
      responseAny.User ??
      legacyUser ??
      null
    );
    const profileRequired = Boolean(
      responseAny.profileRequired ??
      responseAny.ProfileRequired ??
      false
    ) ||
    Boolean(user ? user['profileCompleted'] === false : false) ||
    this.requiresProfileSetup(responseAny, user);
    const isNewUser =
      responseAny.isNewUser ??
      responseAny.IsNewUser ??
      legacyUser?.['isNewUser'] ??
      (user ? user['profileCompleted'] === false : null) ??
      false;

    return {
      ...responseAny,
      success: responseAny.success ?? responseAny.Success ?? false,
      token: this.extractAuthToken(responseAny),
      accessToken: this.extractAuthToken(responseAny),
      tokenType: responseAny.tokenType ?? responseAny.TokenType ?? 'Bearer',
      expiresIn: responseAny.expiresIn ?? responseAny.ExpiresIn ?? null,
      provider: responseAny.provider ?? responseAny.Provider ?? null,
      firebaseUid: responseAny.firebaseUid ?? responseAny.FirebaseUid ?? null,
      profileRequired,
      isNewUser,
      message: responseAny.message ?? responseAny.Message ?? null,
      user,
      userId: responseAny.userId ?? responseAny.UserId ?? user?.id ?? null,
    } as FirebaseLoginResponse;
  }

  private requiresProfileSetup(response: any, user: AuthUser | null): boolean {
    if (response?.profileRequired === true || response?.ProfileRequired === true) {
      return true;
    }

    if (response?.user?.['profileCompleted'] === false || response?.data?.user?.['profileCompleted'] === false) {
      return true;
    }

    return this.hasMissingGrade(this.resolveProfileSource(response, user));
  }

  private hasMissingGrade(profileSource: any): boolean {
    const grade = this.normalizeProfileValue(
      profileSource?.grade ??
      profileSource?.Grade ??
      profileSource?.userdetails?.grade ??
      profileSource?.userDetails?.grade ??
      profileSource?.userdetails?.Grade ??
      profileSource?.userDetails?.Grade
    );

    return !grade;
  }

  private resolveProfileSource(response: any, user: AuthUser | null): any {
    return (
      response?.user ??
      response?.data?.user ??
      response?.data ??
      response?.userdetails ??
      response?.userDetails ??
      user ??
      {}
    );
  }

  private normalizeProfileValue(value: any): string {
    return String(value ?? '').trim();
  }

  private normalizeAuthUser(source: any): AuthUser | null {
    if (!source) {
      return null;
    }

    return {
      ...source,
      id: source.id ?? source.Id ?? source.userId ?? source.UserId ?? null,
      name: source.name ?? source.Name ?? null,
      mobileNumber: source.mobileNumber ?? source.MobileNumber ?? source.mobile ?? source.Mobile ?? null,
      email: source.email ?? source.Email ?? null,
    };
  }
  async checkUserExists(payload: {
    email?: string;
    mobile?: string;
  }): Promise<any> {
    const normalizedPayload = {
      ...(payload.email
        ? { email: String(payload.email).trim().toLowerCase() }
        : {}),
      ...(payload.mobile
        ? { mobile: this.normalizeMobileNumber(String(payload.mobile).trim()) }
        : {}),
    };

    return firstValueFrom(
      this.nodeApiService.Post(endPoints.AUTH_CHECK_USER, normalizedPayload, {
        'Content-Type': 'application/json',
        'X-Portal': 'frontend',
      })
    );
  }
}
